import { z } from 'zod/v3';
import { shorthandToTy, nn } from '../ty/ty';
import { LiteralSchema } from './base';
import _ from 'lodash';

// This is in it's own .js because it turns out to be decently expensive TS
// type checking

// We also never want to _infer_ to super, unless the caller specifies it
export const PRIMITIVE_TYPE_INFERENCE_RULES = [
  ['int', z.number().int()],
  ['float', z.number()],
  ['boolean', z.boolean()],
  ['timestamp', z.date()],
  ['string', z.string()],
];

export const validatorForType = (
  shorthand,
  opts = {
    rules: PRIMITIVE_TYPE_INFERENCE_RULES,
  }
) => {
  const eTy = shorthandToTy(shorthand);
  const { ty, nullable } = eTy;
  const primitiveValidators = Object.fromEntries(opts.rules);

  let outputSchema = null;

  if (ty.k === 'primitive') {
    if (ty.t === 'super') {
      outputSchema = LiteralSchema;
    } else if (ty.t === 'timestamp') {
      outputSchema = z.date({ coerce: true });
    } else {
      outputSchema = primitiveValidators[ty.t];
    }
  }

  if (ty.k === 'array') {
    outputSchema = z.array(validatorForType(ty.t, opts));
  }

  if (ty.k === 'enum') {
    if (ty.t.length > 0) {
      outputSchema = z.enum(ty.t);
    } else {
      outputSchema = nullable ? z.null() : z.never();
    }
  }

  if (ty.k === 'record') {
    outputSchema = z.record(z.string(), validatorForType(nn(ty.t), opts));
  }

  if (ty.k === 'range') {
    outputSchema = z.number().min(ty.start).max(ty.end);
  }

  if (ty.k === 'struct') {
    outputSchema = z.object(
      _.mapValues(ty.fields, (ty) => validatorForType(ty, opts))
    );
  }

  if (!outputSchema) {
    throw new Error(`Logic error on ${JSON.stringify(ty)}`);
  }

  outputSchema = nullable ? outputSchema.nullable() : outputSchema;

  return outputSchema;
};

export const jsonSchemaForType = (shorthand) => {
  const { ty, nullable } = shorthandToTy(shorthand);

  const makeSchema = (schema) => {
    return nullable ? { oneOf: [schema, { type: 'null' }] } : schema;
  };

  if (ty.k === 'primitive') {
    if (ty.t === 'super') {
      return {};
    }

    const typeMap = {
      int: { type: 'integer' },
      float: { type: 'number' },
      boolean: { type: 'boolean' },
      timestamp: { type: 'string', format: 'date-time' },
      string: { type: 'string' },
    };

    return makeSchema(typeMap[ty.t]);
  }

  if (ty.k === 'array') {
    return makeSchema({
      type: 'array',
      items: jsonSchemaForType(ty.t),
    });
  }

  if (ty.k === 'enum') {
    if (ty.t.length > 0) {
      return makeSchema({
        type: 'string',
        enum: ty.t,
      });
    } else {
      return nullable ? { type: 'null' } : { not: {} };
    }
  }

  if (ty.k === 'id') {
    return jsonSchemaForType({
      ty: { k: 'primitive', t: ty.t },
      nullable,
      tags: ty.tags,
    });
  }

  if (ty.k === 'record') {
    return makeSchema({
      type: 'object',
      additionalProperties: jsonSchemaForType(nn(ty.t)),
    });
  }

  if (ty.k === 'range') {
    return makeSchema({
      type: 'number',
      minimum: ty.start,
      maximum: ty.end,
    });
  }

  // Handle object type
  return makeSchema({
    type: 'object',
    required: Object.keys(ty.fields).filter(
      (key) => !shorthandToTy(ty.fields[key]).nullable
    ),
    properties: _.mapValues(ty.fields, jsonSchemaForType),
  });
};
